import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { getOrbitColor, getOrbitTypeName } from '../utils/formatters';

const OrbitVisualizer3D = ({ initialOrbit, targetOrbit, results }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const animationIdRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current) return;

    // ========== SCENE SETUP ==========
    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0e1a);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);
    
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // ========== LIGHTING ==========
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const sunLight = new THREE.DirectionalLight(0xffffff, 0.8);
    sunLight.position.set(100, 50, 100);
    scene.add(sunLight);

    // ========== EARTH ==========
    const EARTH_RADIUS_KM = 6371;
    const EARTH_RADIUS_3D = 10;
    const SCALE = EARTH_RADIUS_3D / EARTH_RADIUS_KM;

    const earthGeometry = new THREE.SphereGeometry(EARTH_RADIUS_3D, 32, 32);
    const earthMaterial = new THREE.MeshPhongMaterial({
      color: 0x1e40af,
      emissive: 0x0c1e3f,
      shininess: 10,
    });
    const earth = new THREE.Mesh(earthGeometry, earthMaterial);
    scene.add(earth);

    // Earth glow
    const glowGeometry = new THREE.SphereGeometry(EARTH_RADIUS_3D * 1.05, 32, 32);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x38bdf8,
      transparent: true,
      opacity: 0.15,
    });
    const earthGlow = new THREE.Mesh(glowGeometry, glowMaterial);
    scene.add(earthGlow);

    // Equator line
    const equatorGeometry = new THREE.RingGeometry(
      EARTH_RADIUS_3D * 1.01,
      EARTH_RADIUS_3D * 1.02,
      64
    );
    const equatorMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.2,
      side: THREE.DoubleSide,
    });
    const equator = new THREE.Mesh(equatorGeometry, equatorMaterial);
    equator.rotation.x = Math.PI / 2;
    scene.add(equator);

    // ========== ORBIT DRAWING FUNCTION ==========
    const createOrbit = (orbitParams, color) => {
      const { altitude, eccentricity, inclination } = orbitParams;
      const a = (EARTH_RADIUS_KM + altitude) * SCALE;
      const ecc = eccentricity || 0;
      const b = a * Math.sqrt(1 - ecc * ecc);
      const c = a * ecc;

      const curve = new THREE.EllipseCurve(-c, 0, a, b, 0, 2 * Math.PI, false, 0);
      const points = curve.getPoints(128);
      const geometry = new THREE.BufferGeometry().setFromPoints(
        points.map(p => new THREE.Vector3(p.x, 0, p.y))
      );

      const material = new THREE.LineBasicMaterial({
        color: new THREE.Color(color),
        transparent: true,
        opacity: 0.8,
      });

      const line = new THREE.Line(geometry, material);
      const incRad = (inclination || 0) * (Math.PI / 180);
      line.rotation.x = Math.PI / 2 - incRad;

      scene.add(line);
      return { line, radius: a };
    };

    // ========== DRAW ORBITS ==========
    const orbit1 = createOrbit(initialOrbit, getOrbitColor(initialOrbit.altitude));
    const orbit2 = createOrbit(targetOrbit, getOrbitColor(targetOrbit.altitude));

    // ========== TRANSFER TRAJECTORY ==========
    if (results) {
      const r1 = (EARTH_RADIUS_KM + initialOrbit.altitude) * SCALE;
      const r2 = (EARTH_RADIUS_KM + targetOrbit.altitude) * SCALE;

      const aTransfer = (r1 + r2) / 2;
      const eccTransfer = Math.abs(r2 - r1) / (r1 + r2);
      const bTransfer = aTransfer * Math.sqrt(1 - eccTransfer * eccTransfer);
      const cTransfer = aTransfer * eccTransfer;

      const transferCurve = new THREE.EllipseCurve(
        -cTransfer, 0,
        aTransfer, bTransfer,
        0, Math.PI, 
        r2 < r1,
        0
      );

      const transferPoints = transferCurve.getPoints(64);
      const transferGeometry = new THREE.BufferGeometry().setFromPoints(
        transferPoints.map(p => new THREE.Vector3(p.x, 0, p.y))
      );

      const transferMaterial = new THREE.LineDashedMaterial({
        color: 0xfbbf24,
        dashSize: 2,
        gapSize: 1,
      });

      const transferLine = new THREE.Line(transferGeometry, transferMaterial);
      transferLine.computeLineDistances();

      const incRad = (initialOrbit.inclination || 0) * (Math.PI / 180);
      transferLine.rotation.x = Math.PI / 2 - incRad;

      if (r2 < r1) {
        transferLine.rotation.z = Math.PI;
      }

      scene.add(transferLine);

      // BURN POINTS
      const createBurnMarker = (radius, angle, color) => {
        const x = radius * Math.cos(angle);
        const z = radius * Math.sin(angle);
        const marker = new THREE.Mesh(
          new THREE.SphereGeometry(EARTH_RADIUS_3D * 0.15, 16, 16),
          new THREE.MeshBasicMaterial({ color })
        );
        marker.position.set(x, 0, z);
        marker.rotation.x = Math.PI / 2 - incRad;
        scene.add(marker);
      };

      createBurnMarker(r1, 0, 0x00ff00);
      createBurnMarker(r2, Math.PI, 0xff0000);
    }

    // ========== CAMERA POSITION ==========
    const maxRadius = Math.max(orbit1.radius, orbit2.radius);
    camera.position.set(maxRadius * 1.8, maxRadius * 1.2, maxRadius * 1.8);
    camera.lookAt(0, 0, 0);

    // ========== ANIMATION LOOP ==========
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      earth.rotation.y += 0.002;
      earthGlow.rotation.y += 0.002;
      camera.position.x = camera.position.x * Math.cos(0.001) - camera.position.z * Math.sin(0.001);
      camera.position.z = camera.position.z * Math.cos(0.001) + camera.position.x * Math.sin(0.001);
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!mountRef.current) return;
      const W = mountRef.current.clientWidth;
      const H = mountRef.current.clientHeight;
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
      renderer.setSize(W, H);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationIdRef.current);
      if (mountRef.current && rendererRef.current) mountRef.current.removeChild(rendererRef.current.domElement);
      if (rendererRef.current) rendererRef.current.dispose();
    };
  }, [initialOrbit, targetOrbit, results]);

  return (
    <div className="relative w-full h-full bg-dark-900 rounded-lg overflow-hidden">
      <div ref={mountRef} className="w-full h-[500px]" />
      
      {/* Labels */}
      <div className="absolute bottom-4 left-4 space-y-2 text-sm pointer-events-none">
        <div className="flex items-center space-x-2 bg-dark-900/80 px-3 py-1 rounded">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getOrbitColor(initialOrbit.altitude) }} />
          <span className="text-gray-300">
            Initial: {initialOrbit.altitude} km ({getOrbitTypeName(initialOrbit.altitude)})
          </span>
        </div>

        <div className="flex items-center space-x-2 bg-dark-900/80 px-3 py-1 rounded">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getOrbitColor(targetOrbit.altitude) }} />
          <span className="text-gray-300">
            Target: {targetOrbit.altitude} km ({getOrbitTypeName(targetOrbit.altitude)})
          </span>
        </div>

        {results && (
          <div className="flex items-center space-x-2 bg-dark-900/80 px-3 py-1 rounded border border-yellow-500/30">
            <div className="w-3 h-3 rounded-full bg-yellow-500 shadow-[0_0_8px_rgba(251,191,36,0.5)]" />
            <span className="text-gray-300">
              {/* ЗДЕСЬ ИСПРАВЛЕН ВЫВОД: показываем тип и дистанцию в км */}
              Transfer: {Math.round((initialOrbit.altitude + targetOrbit.altitude) / 2)} km ({
          (() => {
            const type = results.transfer_type || results.transfer_type_used || 'hohmann';
            // Делаем первую букву заглавной
            return type.charAt(0).toUpperCase() + type.slice(1);
          })()
        })
            </span>
          </div>
        )}
      </div>

      {/* Orbit parameters display */}
      <div className="absolute top-4 right-4 bg-dark-900/80 px-4 py-2 rounded text-xs space-y-1 pointer-events-none">
        <p className="text-primary-400 font-semibold uppercase tracking-wider">Telemetry</p>
        <p className="text-gray-300">Ecc: {initialOrbit.eccentricity.toFixed(2)} → {targetOrbit.eccentricity.toFixed(2)}</p>
        <p className="text-gray-300">Inc: {initialOrbit.inclination.toFixed(1)}° → {targetOrbit.inclination.toFixed(1)}°</p>
      </div>
    </div>
  );
};

export default OrbitVisualizer3D;